import { useAtomValue } from 'jotai';
import { useContext, useEffect, useState } from 'react';

import {
  getElectionById,
  getElectionsContaining,
  LIST_1093_HAS_ELECTORATES,
} from '@/apis/elections';
import {
  ElectionRecordResult,
  isTemplatePlaceholder,
  RosterCriteria,
  TemplatePlaceholder,
} from '@/types';
import { domainCodeAtom } from '@australiangreens/ag-internal-components';
import { useAuth0 } from '@auth0/auth0-react';
import { AccordionField } from '@components/DrawerAccordion';
import { Search } from '@mui/icons-material';

import TemplateCapableFetchAutocomplete, {
  TemplateCapableFetchAutocompleteChangeReason,
} from '@/components/TemplateCapableFetchAutocomplete';
import TemplateCapableSingleAutocomplete, {
  TemplateCapableSingleAutocompleteChangeReason,
} from '@pages/AdvancedSearchListCreatorPage/customWidgets/templateCapableComponents/TemplateCapableSingleAutocomplete';
import { SearchTemplatePageContext } from '@/context/SearchTemplateContext';
import { labelSorting } from '@/util/labelSorting';
import RosterCriteriaForm from './RosterCriteriaForm';
import {
  ROSTER_TYPES_WITH_IDS,
  RosterTypeRecordResult,
} from '@/constants/filterCriteria/roster';
import { ElectorateRecordResult } from '@/types/filterCriteriaRecords';

type Props = {
  electionOnChange: (
    newValue: ElectionRecordResult | null | TemplatePlaceholder,
    reason: TemplateCapableSingleAutocompleteChangeReason
  ) => void;
  electionValue: ElectionRecordResult | null | TemplatePlaceholder;
  electorateOnChange: (
    newValue: ElectionRecordResult[] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  electorateValue: ElectorateRecordResult[] | TemplatePlaceholder;
  rosterTypeOnChange: (
    newValue: RosterTypeRecordResult[] | TemplatePlaceholder,
    reason: TemplateCapableFetchAutocompleteChangeReason
  ) => void;
  rosterTypeValue: RosterTypeRecordResult[] | TemplatePlaceholder;
  rosterCriteriaOnChange: (newValue: RosterCriteria, field: keyof RosterCriteria) => void;
  rosterCriteriaValue: RosterCriteria;
};

const Roster = ({
  electionOnChange,
  electionValue,
  electorateValue,
  electorateOnChange,
  rosterTypeValue,
  rosterTypeOnChange,
  rosterCriteriaOnChange,
  rosterCriteriaValue,
}: Props) => {
  const isOnTemplatePage = useContext(SearchTemplatePageContext);

  const domainCode = useAtomValue(domainCodeAtom);
  const { getAccessTokenSilently } = useAuth0();
  const [electorateRecords, setElectorateRecords] = useState<{ id: number; label: string }[]>([]);

  const lookupByElectionName = async (lookupValue: string) => {
    const token = await getAccessTokenSilently();

    // One can only get elections with electorates for it.

    const returnData = await getElectionsContaining(
      token,
      lookupValue,
      domainCode,
      LIST_1093_HAS_ELECTORATES
    );
    return returnData
      .map((x) => {
        return {
          id: x.id,
          label: x.name,
        };
      })
      .sort(labelSorting);
  };

  const electionValueIsPlaceholder = isTemplatePlaceholder(electionValue);

  /* eslint-disable react-you-might-not-need-an-effect/no-derived-state -- load electorates for the selected election */
  useEffect(() => {
    const fetchData = async () => {
      if (electionValue && !electionValueIsPlaceholder) {
        const token = await getAccessTokenSilently();
        const returnData = await getElectionById(token, electionValue?.id || 0);
        const response = returnData.electorates
          .map((x) => {
            return {
              id: x.id,
              label: x.name,
            };
          })
          .sort(labelSorting);
        setElectorateRecords(response);
      } else {
        setElectorateRecords([]);
      }
    };
    fetchData();
  }, [electionValue, getAccessTokenSilently, electionValueIsPlaceholder]);

  const showLowerSection =
    electionValueIsPlaceholder ||
    (electionValue && electorateRecords && electorateRecords.length > 0);

  const showNoRosterDataWarning =
    !electionValueIsPlaceholder &&
    electionValue &&
    electorateRecords &&
    electorateRecords.length === 0;

  return (
    <>
      <AccordionField title="Election">
        <TemplateCapableSingleAutocomplete
          onChange={electionOnChange}
          value={electionValue}
          label={'Find an election'}
          lookup={lookupByElectionName}
          sx={{ width: '100%' }}
          popupIcon={<Search />}
          disableIconFlip
          isOnTemplatePage={isOnTemplatePage}
          templateBuilderDisplayName="Roster filter: Election"
        />
      </AccordionField>
      {!electionValue && <AccordionField title={'Select an election to see more roster options'} />}
      {showNoRosterDataWarning && (
        <AccordionField title={`No roster data available for ${electionValue.label}`} />
      )}
      {showLowerSection && (
        <>
          <AccordionField
            title={`Options available ${electionValueIsPlaceholder ? '' : `for ${electionValue.label}`}`}
          />
          <AccordionField
            title={
              isTemplatePlaceholder(electorateValue) || electorateValue.length > 0
                ? 'Electorates'
                : 'Electorates: Any'
            }
          >
            <TemplateCapableFetchAutocomplete
              /* Every time we change the election, we change the key to change electorate drop down from scratch */
              key={electionValueIsPlaceholder ? 0 : electionValue.id}
              preLoadedOptions={electionValueIsPlaceholder ? [] : electorateRecords}
              minLength={0}
              label="Specify electorate(s)"
              value={electorateValue}
              onChange={electorateOnChange}
              data-testid="getelectorates"
              loadingText="Looking for electorates..."
              noOptionsText="No electorates found"
              isOnTemplatePage={isOnTemplatePage}
              templateBuilderDisplayName="Roster filter: Electorates"
            />
          </AccordionField>
          <AccordionField
            title={
              isTemplatePlaceholder(rosterTypeValue)
                ? 'Roster Types'
                : rosterTypeValue.length
                  ? 'Roster Types'
                  : 'Roster Types: Any'
            }
          >
            <TemplateCapableFetchAutocomplete
              minLength={0}
              label="Specify Roster type(s)"
              preLoadedOptions={Object.values(ROSTER_TYPES_WITH_IDS)}
              value={rosterTypeValue}
              onChange={rosterTypeOnChange}
              data-testid="rostertypes"
              loadingText="Looking for roster types..."
              noOptionsText="No roster types found"
              isOnTemplatePage={isOnTemplatePage}
              templateBuilderDisplayName="Roster filter: Roster types"
            />
          </AccordionField>
          <RosterCriteriaForm
            value={rosterCriteriaValue}
            onChange={rosterCriteriaOnChange}
            isOnTemplatePage={isOnTemplatePage}
          />
        </>
      )}
    </>
  );
};

export default Roster;
